import { UnarchiveOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment } from "react";
import {
    Controller,
    FormProvider,
    useForm,
    useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmAndCancelButtons from "../../../../../../components/buttons/ConfirmAndCancelButtons";
import { SnackbarContext } from "../../../../../../contexts/Snackbar";
import {
    AVAILABLE_BOXES,
    UNARCHIVE_BOX,
} from "../../../../../../enums/ApiPaths";
import {
    ARCHIVE_LIST,
    AVAILABLE_BOXES_KEY,
    BOXES_LIST_KEY,
    SINGLE_BOX_KEY,
} from "../../../../../../enums/QueryKeys";
import { useFetch } from "../../../../../../hooks/Fetching";
import { snackbarSeverity } from "../../../../../../hooks/useSnackbar";
import api from "../../../../../../utils/api/authApi";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import NotFound from "../../../../components/display/NotFound";

export default function UnarchiveInpatientStay({ pet }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: inpatientId } = useParams();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [openDialog, setOpenDialog] = React.useState(false);
  const queryClient = useQueryClient();
  const methods = useForm({ defaultValues: { boxId: "" } });

  const { data: availableBoxes = [], isLoading: loadingBoxes } = useFetch(
    AVAILABLE_BOXES,
    pet?.id,
    [AVAILABLE_BOXES_KEY, pet?.id],
    openDialog,
  );

  function handleDialog() {
    setOpenDialog((prev) => !prev);
    if (openDialog) methods.reset();
  }

  async function onSubmit(data) {
    const boxId = data.boxId;
    try {
      await api.post(`${UNARCHIVE_BOX}${boxId}/${inpatientId}`);
      await queryClient.invalidateQueries({ queryKey: [ARCHIVE_LIST] });
      await queryClient.invalidateQueries({ queryKey: [BOXES_LIST_KEY] });
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (part) =>
              typeof part === "string" && part.startsWith(SINGLE_BOX_KEY),
          ),
      });
      handleSnackbar("ActionWasSuccessful", snackbarSeverity.SUCCESS);
      setOpenDialog(false);
      navigate(-1);
    } catch (e) {
      handleSnackbar(e?.response?.data, snackbarSeverity.ERROR);
    }
  }

  const noBoxes = !availableBoxes || availableBoxes.length == 0;

  return (
    <>
      <ActionRow
        icon={<UnarchiveOutlined />}
        label={t("UnarchiveInpatientStay")}
        description={t("UnarchiveInpatientStayDescription")}
        onClick={handleDialog}
      />

      <Dialog
        open={openDialog}
        onClose={handleDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {noBoxes ? (
          <Fragment>
            <Header handleDialog={handleDialog} />
            <NotFound
              title={t("NoBoxesAvailable")}
              subtitle={t("AddMoreBoxesFirstOrCheckIfPetAlreadyInBox")}
            />
          </Fragment>
        ) : (
          <Fragment>
            <Header handleDialog={handleDialog} />
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Form
                  availableBoxes={availableBoxes}
                  handleDialog={handleDialog}
                  loadingBoxes={loadingBoxes}
                />
              </form>
            </FormProvider>
          </Fragment>
        )}
      </Dialog>
    </>
  );
}

function Form({ handleDialog, loadingBoxes, availableBoxes }) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <Fragment>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info">{t("UnarchiveStayInfo")}</Alert>
            {loadingBoxes ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Controller
                name="boxId"
                control={control}
                rules={{ required: t("EmptyField") }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.boxId}>
                    <InputLabel>{t("SelectBox")}</InputLabel>
                    <Select {...field} label={t("SelectBox")}>
                      {availableBoxes.map((box) => (
                        <MenuItem key={box.id} value={box.id}>
                          {box.name} —{" "}
                          {box.numberOfPets === 1
                            ? t("petsCount", { count: 1 })
                            : t("petsCount_plural", {
                                count: box.numberOfPets,
                              })}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.boxId?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
        >
          <ConfirmAndCancelButtons
            confirmTitle="Unarchive"
            cancelTitle="Cancel"
            onCancel={handleDialog}
            confirmButtonType="submit"
            confirmButtonProps={{
              disabled: loadingBoxes,
            }}
          />
        </DialogActions>
      </Fragment>
    </>
  );
}

function Header({ handleDialog }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <UnarchiveOutlined color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700}>
          {t("UnarchiveInpatientStay")}
        </Typography>
      </Stack>
      <IconButton onClick={handleDialog} size="small" color="error">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
